import Moment from "moment";
import { UserDefinitionWindowKinds } from "../UserDefinitionWindowKinds";
import { AbstractPlainTextItem } from "./AbstractPlainTextItem";
import { TypeKeys } from "./TypeKeys";
import { IEmptyValidatable } from "./IEmptyValidatable";
import { INameGroupEmptyValidatable } from "./INameGroupEmptyValidatable";
import { IUserDefinitionItem } from "./IUserDefinitionItem";
import { SelectionModeKinds } from "../SelectionModeKinds";
import { IHisInformationSelectable } from "./IHisInformationSelectable";
import { FuzzyDate } from "../../system/FuzzyDate";
import { SeparatorKinds } from "../SeparatorKinds";
import { IPaintableOnEditing } from "./IPaintableOnEditing";
import { PaintStyle } from "../../drawing";
import { HisItemParameter } from "../../his/behavior/HisItemParameter";
import { IExternalScriptAssociatable } from "./IExternalScriptAssociatable";
import { IRelatedExternalScriptExecutable } from "./IRelatedExternalScriptExecutable";
import { IPatientProfileInformationApplicable } from "./IPatientProfileInformationApplicable";
import { PatientProfileInformation } from "../../patient/PatientProfileInformation";
import { PatientProfileItem } from "../../patient/PatientProfileItem";
import { HisInformationTextValue, HisInformationDateTimeValue, IHisInformationValue } from "../../his/HisInformationValue";
import { DateInputMethodKinds } from "../inputMethodKinds/DateInputMethodKinds";
import { IPasteRequestable } from "./IPasteRequestable";
import { IPasteApplicable } from "./IPasteApplicable";
import { RelationalActionParameter } from "./RelationalActionParameter";
import { DateFormatter, LastUpdaterManagedValue } from "../../system";
import { IFontSizeAutoAdjustable } from "./IFontSizeAutoAdjustable";
import { AutoNotePageKinds } from "../AutoNotePageKinds";
import { ITextCopyable } from "./ITextCopyable";
import { IItem } from "./IItem";
import { instanceOfIDateCalculateAssociatable, IDateCalculateAssociatable } from "./IDateCalculateAssociatable";
import { ICalculatedValueValidatable } from "./ICalculatedValueValidatable";
import { DateInputMethodOptionKinds } from "../inputMethodOptionKinds";
import { InitialSettingKinds } from "../InitialSettingKinds";
import { IInitialApplicable } from "./IInitialApplicable";
import { OperatorKinds } from "../OperatorKinds";
import { ObjectId } from "../ObjectId";
import { IDocumentDataDraggable } from "./IDocumentDataDraggable";
import { IDocumentDataDragParameter } from "../../../components/dragging/IDocumentDataDragParameter";
import { INumberingAssosiatable } from "./INumberingAssosiatable";
import { ISubNameSpecifiable } from "./ISubNameSpecifiable";
import { IDateLinkRequestable } from "./IDateLinkRequestable";
import { IDateLinkApplicable } from "./IDateLinkApplicable";
import { INaturalTextSpecifiable } from "./INaturalTextSpecifiable";
import { IValueForDateCompareExtractable } from "./IValueForDateCompareExtractable";

// TODO: とりあえずPlainTextItemにしておく.Docu用のDateTime型(中旬とか対応できる奴...)を定義してTValue指定？
export class DateItem extends AbstractPlainTextItem implements
  IEmptyValidatable,
  INameGroupEmptyValidatable,
  IUserDefinitionItem,
  IHisInformationSelectable,
  IPaintableOnEditing,
  IExternalScriptAssociatable,
  IRelatedExternalScriptExecutable,
  IPatientProfileInformationApplicable,
  IPasteRequestable,
  IFontSizeAutoAdjustable,
  IDateCalculateAssociatable,
  ICalculatedValueValidatable,
  IInitialApplicable,
  ITextCopyable,
  IDocumentDataDraggable,
  INumberingAssosiatable,
  ISubNameSpecifiable,
  IDateLinkRequestable,
  IDateLinkApplicable,
  INaturalTextSpecifiable,
  IValueForDateCompareExtractable {
  constructor(value: any) {
    super(TypeKeys.DateItem, value);
  }

  public paintStyleOnEditing: PaintStyle = null;
  public groupKey: string = "";
  public enableSingleEmptyCheck: boolean = false;
  public enableEmptyCheckConfirmation: boolean = false;
  public alertGroupKey: string = null;
  public format: string = "Y 年 M 月 D日";
  public userDefinitionKey: string = null;
  public userDefinitionSelectionMode: SelectionModeKinds = SelectionModeKinds.Row;
  public itemName: string = null;
  public userDefinitionWindowKind: UserDefinitionWindowKinds = null;
  public isHisInfo: boolean = false;
  public displayName: string = null;
  public groupName: string = null;
  public subName: string = null;    // 転記で使用する模様
  public separatorKind: SeparatorKinds = null;
  public hisItemParameter: HisItemParameter = null;
  public isDialogMultiSelectable: boolean = false;
  /** 登録、または一時保存までの編集中の間、選択されている項目を保持する */
  public selectedItems: any[] = null;
  public enableApproximateInput: boolean = true;
  /** カレンダー表示からの入力を許可するかどうか */
  public isCalendarInputtable: boolean;
  /** HIS情報を選択ダイアログからの入力を許可するかどうか */
  public isHisInfoSelectInputable: boolean = false;
  /** 入力方法 */
  private _inputMethodKind: LastUpdaterManagedValue<DateInputMethodKinds, boolean>;
  get inputMethodKind(): DateInputMethodKinds {
    return this._inputMethodKind.value;
  }
  set inputMethodKind(value: DateInputMethodKinds) {
    this.updateInputMethodKind(value, true);
  }
  private updateInputMethodKind(value: DateInputMethodKinds, updater: boolean) {
    // constructorや初期値が反映される前に呼ばれる場合があるため、生成はここで行う
    if (this._inputMethodKind) {
      this._inputMethodKind.update(value, updater);
    } else {
      this._inputMethodKind = new LastUpdaterManagedValue(value, updater); 
    }
  }
  /** 入力方法の最終更新が外部から行われたか */
  get isInputMethodKindLastUpdatedExternally(): boolean {
    return this._inputMethodKind.lastUpdater;
  }
  /** 入力方法の選択肢の種別 */
  public inputMethodOptionKind: DateInputMethodOptionKinds;
  /** 初期入力方法(カレンダー入力、平成直接入力) */
  public initialInputMethodKind: DateInputMethodKinds; // 徳大では使用しない
  public pasteDestinations: RelationalActionParameter[] = [];
  public parentAlertInformations: RelationalActionParameter[] = [];
  public calculateInformations: RelationalActionParameter[] = [];
  /** フォントサイズ自動調整機能 */
  public enabledAutoAdjustFontSize: boolean = false;
  public autoNotePageSize: number = null;
  public autoNotePageKind: AutoNotePageKinds = null;
  /** 初期値を設定するか */
  public isInitial: boolean;
  /** 初期値の種別（先頭:1/末尾:2/作成日:3） */
  public initialSettingKind: InitialSettingKinds;
  public shouldSelectHospitalizationInitialValueHospitalizedOnly: boolean;
  public enableNameGroupEmptyCheck: boolean = false;

  public linkedValue: string;
  public userDefineLinkActions: RelationalActionParameter[] = [];
  public userDefineSearchActions: RelationalActionParameter[] = [];
  public dateLinkDestinations: RelationalActionParameter[] = [];
 
  /** 英語フォーマット対応 */
  public manualFormat: string = null;
  public manualFormatLanguage: string = null;

  public isNaturalViewEnable: boolean;
  public naturalText: string;

  protected createCloneInstance(): DateItem {
    return new DateItem(this.originalValue);
  }

  protected applyMembers(clone: DateItem): void {
    super.applyMembers(clone);
    clone.paintStyleOnEditing = this.cloneValue(this.paintStyleOnEditing);
    clone.groupKey = this.cloneValue(this.groupKey);
    clone.enableSingleEmptyCheck = this.cloneValue(this.enableSingleEmptyCheck);
    clone.enableEmptyCheckConfirmation = this.cloneValue(this.enableEmptyCheckConfirmation);
    clone.alertGroupKey = this.cloneValue(this.alertGroupKey);
    clone.format = this.cloneValue(this.format);
    clone.userDefinitionKey = this.cloneValue(this.userDefinitionKey);
    clone.userDefinitionSelectionMode = this.cloneValue(this.userDefinitionSelectionMode);
    clone.itemName = this.cloneValue(this.itemName);
    clone.userDefinitionWindowKind = this.cloneValue(this.userDefinitionWindowKind);
    clone.isHisInfo = this.cloneValue(this.isHisInfo);
    clone.displayName = this.cloneValue(this.displayName);
    clone.groupName = this.cloneValue(this.groupName);
    clone.subName = this.cloneValue(this.subName);
    clone.separatorKind = this.cloneValue(this.separatorKind);
    clone.hisItemParameter = this.cloneValue(this.hisItemParameter);
    clone.isDialogMultiSelectable = this.cloneValue(this.isDialogMultiSelectable);
    clone.selectedItems = this.cloneValue(this.selectedItems);
    clone.enableApproximateInput = this.cloneValue(this.enableApproximateInput);
    clone.isCalendarInputtable = this.cloneValue(this.isCalendarInputtable);
    clone.isHisInfoSelectInputable = this.cloneValue(this.isHisInfoSelectInputable);
    clone._inputMethodKind = this.cloneValue(this._inputMethodKind);
    clone.inputMethodOptionKind = this.cloneValue(this.inputMethodOptionKind);
    clone.initialInputMethodKind = this.cloneValue(this.initialInputMethodKind);
    clone.pasteDestinations = this.cloneValue(this.pasteDestinations);
    clone.parentAlertInformations = this.cloneValue(this.parentAlertInformations);
    clone.calculateInformations = this.cloneValue(this.calculateInformations);
    clone.enabledAutoAdjustFontSize = this.cloneValue(this.enabledAutoAdjustFontSize);
    clone.autoNotePageSize = this.cloneValue(this.autoNotePageSize);
    clone.autoNotePageKind = this.cloneValue(this.autoNotePageKind);
    clone.isInitial = this.cloneValue(this.isInitial);
    clone.initialSettingKind = this.cloneValue(this.initialSettingKind);
    clone.shouldSelectHospitalizationInitialValueHospitalizedOnly = this.cloneValue(this.shouldSelectHospitalizationInitialValueHospitalizedOnly);
    clone.enableNameGroupEmptyCheck = this.cloneValue(this.enableNameGroupEmptyCheck);
    clone.linkedValue = this.cloneValue(this.linkedValue);
    clone.userDefineLinkActions = this.cloneValue(this.userDefineLinkActions);
    clone.userDefineSearchActions = this.cloneValue(this.userDefineSearchActions);
    clone.dateLinkDestinations = this.cloneValue(this.dateLinkDestinations);
    clone.manualFormat = this.cloneValue(this.manualFormat);
    clone.manualFormatLanguage = this.cloneValue(this.manualFormatLanguage);
    clone.isNaturalViewEnable = this.cloneValue(this.isNaturalViewEnable);
    clone.naturalText = this.cloneValue(this.naturalText);
  }

  initialize(
    itemAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean)) => IItem,
    itemsAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean)) => IItem[]
  ): IItem {
    return this.getUpdatedClone<DateItem>(
      s => super.initialize(itemAccessor, itemsAccessor) as DateItem,
      s => s.createReevaluatedCloneOnInitialize(itemAccessor),
    );
  }

  private createReevaluatedCloneOnInitialize(itemAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean)) => IItem): DateItem {
    return this.shouldCalculate
      ? this.createReevaluatedClone(itemAccessor)
      : null;
  }

  handleItemUpdated(
    originalItem: IItem,
    updatedItem: IItem,
    isInSameSection: boolean,
    itemAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean), isSameSectionOnly?: boolean) => IItem,
    itemsAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean), isSameSectionOnly?: boolean) => IItem[]
  ): IItem {
    return this.getUpdatedClone<DateItem>(
      s => super.handleItemUpdated(originalItem, updatedItem, isInSameSection, itemAccessor, itemsAccessor) as DateItem,
      s => s.createReevaluatedCloneOnItemUpdated(updatedItem, itemAccessor),
    );
  }

  private createReevaluatedCloneOnItemUpdated(updatedItem: IItem, itemAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean)) => IItem): DateItem {
    return (this.shouldCalculate && this.isCalculateTarget(updatedItem))
      ? this.createReevaluatedClone(itemAccessor)
      : null;
  }

  handleOwnUpdated(
    originalItem: IItem,
    itemAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean), isSameSectionOnly?: boolean) => IItem,
    itemsAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean), isSameSectionOnly?: boolean) => IItem[]
  ): IItem {
    return this.getUpdatedClone<DateItem>(
      s => super.handleOwnUpdated(originalItem, itemAccessor, itemsAccessor) as DateItem,
      s => s.createReevaluatedCloneOnOwnUpdated(originalItem, itemAccessor),
    );
  }

  private createReevaluatedCloneOnOwnUpdated(originalItem: IItem, itemAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean)) => IItem): DateItem {
    return (this.shouldCalculate && this.isCalculateInputChanged(originalItem))
      ? this.createReevaluatedClone(itemAccessor)
      : null;
  }

  private isCalculateInputChanged(originalItem: IItem): boolean {
    return (originalItem instanceof DateItem)
        && (originalItem.shouldCalculate !== this.shouldCalculate);
  }

  get shouldInputFree(): boolean {
    return this.inputMethodKind === DateInputMethodKinds.FreeInput;
  }

  set shouldInputFree(value: boolean) {
    this.inputMethodKind = this.toDefaultInputMethodKind(value);
  }

  private toDefaultInputMethodKind(shouldInputFree: boolean): DateInputMethodKinds {
    return shouldInputFree
      ? DateInputMethodKinds.FreeInput
      : DateInputMethodOptionKinds.getDefualtInputMethodKind(this.inputMethodOptionKind);
  }

  get shouldInputCalendar(): boolean {
    return this.inputMethodKind === DateInputMethodKinds.Calendar;
  }

  get shouldCalculate(): boolean {
    return this.inputMethodKind === DateInputMethodKinds.Calculate;
  }

  private isCalculateTarget(updatedItem: IItem): boolean {
    if (!updatedItem) { return false; }
    for (const info of this.calculateInformations) {
      if (this.isRelatinalKeyMatch(info, updatedItem)) {
        return true;
      }
    }
    return false;
  }

  private isRelatinalKeyMatch(info: RelationalActionParameter, item: IItem): boolean {
    if (!item || !info) { return false; }
    if (info.itemType !== "1") {
      // 1:可変値
      // 2:固定値
      return false;
    }
    if (!info.key) {
      console.warn("計算対象を識別するためのキーがありません。");
      return false;
    }
    if (!instanceOfIDateCalculateAssociatable(item)) { return false; }
    return item.isHisInfo
      ? (info.key === item.groupKey)
      : (info.key === item.name);
  }

  private createReevaluatedClone(itemAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean)) => IItem): DateItem {
    const date = this.evaluate(itemAccessor);
    if (date) {
      // TODO:計算結果が同じ場合の対応
      return this.createUpdatedClone<DateItem>(s => {
        s.value.value = date;
        s.value.linkedDate = null;
      });
    }
    // 計算結果がnull
    if (!this.value.value) { return null; }
    return this.createUpdatedClone<DateItem>(s => {
      s.value.value = null;
      s.value.linkedDate = null;
    });
  }

  private evaluate(itemAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean)) => IItem): Date {
    let result: Date = null;
    for (const info of this.calculateInformations) {
      // 算出基準のオブジェクトにはoperatorが設定されおらず、かつ先頭に位置しているはず
      if (info.operator === null || info.operator === undefined) {
        const found = itemAccessor(item => {
          return this.isRelatinalKeyMatch(info, item);
        });
        // 算出基準は日付オブジェクト
        if (!found || !(found instanceof DateItem) || !found.value || !(found.value instanceof FuzzyDate) || !(found.value.value instanceof Date)) {
          return null;
        }
        result = found.value.value;
        continue;
      }
      if (!result) { return null; }
      const additionalValue = this.getAdditionalDays(itemAccessor, info);
      if (additionalValue === null || additionalValue === undefined) { return null; }
      result = Moment(result).add(additionalValue, "days").toDate();
    }
    return result;
  }

  private getAdditionalDays(itemAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean)) => IItem, info: RelationalActionParameter): number {
    if (info.itemType === "2") {
      // 固定値
      const converted = Number(info.value);
      return (info.operator === OperatorKinds.Minus)
        ? converted * -1
        : converted;
    }
    const found = itemAccessor(item => {
      return this.isRelatinalKeyMatch(info, item);
    });
    if (!instanceOfIDateCalculateAssociatable(found)) { return null; }
    const value = found.value;
    if (value === null || value === undefined || value === "" || !isFinite(value)) { return null; }
    const convertedValue = Number(value);
    return (info.operator === OperatorKinds.Minus)
    ? convertedValue * -1
    : convertedValue;
}

  isPatientProfileKeyMatched(key: string): boolean {
    return (this.name === key) && !this.isHisInfo;
  }

  get isEmpty(): boolean {
    if (!this.value) { return true; }
    const fuzzyDate = this.value as FuzzyDate;
    return fuzzyDate.isEmptyOrZeroLength;
  }

  get isValueEmpty(): boolean {
    if (!this.value) { return true; }
    const fuzzyDate = this.value as FuzzyDate;
    return fuzzyDate.isEmpty;
  }

  get isCalculatedValueEmpty(): boolean {
    return this.isEmpty;
  }

  getGroupSelectKey(): string {
    return null;
  }

  get hasValue(): boolean {
    return !this.isEmpty;
  }

  protected valueToText(value: any): string {
    return (value instanceof FuzzyDate)
      ? value.toString(this.format, this.manualFormat, this.manualFormatLanguage)
      : "";
  }

  setHisInfoItems(hisInfoValue: IHisInformationValue, shouldSkipInputMethodKindUpdate: boolean): void {
    const isTime = (this.value as FuzzyDate).isTime;
    const value
      = (hisInfoValue instanceof HisInformationTextValue) ? this.fromHisInfoTextValue(hisInfoValue)
      : (hisInfoValue instanceof HisInformationDateTimeValue) ? this.fromHisInfoDateTimeValue(hisInfoValue)
      : null;
    this.value = new FuzzyDate(value, isTime);
    if (!shouldSkipInputMethodKindUpdate) {
      this.changeToCalendarInputWhenEmptyText();
    }
  }

  private fromHisInfoTextValue(hisInfoValue: HisInformationTextValue): string {
    return hisInfoValue.data.join(this.getDelimitor(this.separatorKind));
  }
  
  private fromHisInfoDateTimeValue(hisInfoValue: HisInformationDateTimeValue): string | Date {
    if (hisInfoValue.data.length === 0) {
      return null;  // HIS情報がない場合は空文字列でなくnullを設定する.(フリー入力で空文字入力された時と区別付かないので.)
    }
    if (hisInfoValue.data.length === 1) {
      return hisInfoValue.data[0];
    }
    const value = hisInfoValue.data.map(x => DateFormatter.format(x, this.format, this.manualFormat, this.manualFormatLanguage));
    return value.join(this.getDelimitor(this.separatorKind));
  }
  
  /** 入力されている値が空文字ならカレンダー入力に変更 */
  private changeToCalendarInputWhenEmptyText(): void {
    if (this.shouldChangeInputTypeToFreeInput) {
      this.inputMethodKind = DateInputMethodKinds.Calendar;
    }
  }

  /** 入力方法をカレンダー入力にするべきか */
  get shouldChangeInputTypeToFreeInput(): boolean {
    if (this.inputMethodOptionKind === DateInputMethodOptionKinds.HisFreeInput) {
      return this.isEmpty;
    }
    return false;
  }

  public setUserDefinitionValues(values: string[]): void {
    if (values.length === 1 && values[0].match(/^[0-9]{8}$/)) {
      this.value = new FuzzyDate(this.textToDate(values[0]), (this.value as FuzzyDate).isTime);
      return;
    }
    // V1で複数の日付を選択したときは、指定のフォーマットに変換された後の文字列が連結されて表示される。
    const delimitor = this.getDelimitor(this.separatorKind);
    const joinedText = values
      .map(v => this.formatDateText(v))
      .join(delimitor);
    this.value = new FuzzyDate(joinedText, (this.value as FuzzyDate).isTime);
  }

  private formatDateText(dateText: string): string {
    if (!dateText.match(/^[0-9]{8}$/)) { return dateText; }
    const fuzzyDate = new FuzzyDate(this.textToDate(dateText), (this.value as FuzzyDate).isTime);
    return fuzzyDate.toString(this.format, this.manualFormat, this.manualFormatLanguage);
  }

  getValueForExternalScript(): string {
    return (this.value instanceof FuzzyDate)
      ? this.value.isTime
        ? (this.value.isEmptyOrZeroLength ? "000000" : this.value.toString("HHmmss"))
        : (this.value.isEmptyOrZeroLength ? "00000000" : this.value.toString("YYYYMMDD"))
      : this.value;
  }

  setValueFromExternalScript(value: string): void {
    const fuzzyDate = this.value as FuzzyDate;
    const newValue = new FuzzyDate(fuzzyDate.isTime ? this.textToTime(value) : this.textToDate(value), fuzzyDate.isTime);
    if (!newValue.isEmptyOrZeroLength && newValue.isFuzzy) {
      newValue.value = value;
    } else {
      this._inputMethodKind.update(this.toDefaultInputMethodKind(false), false);
    }
    this.value = newValue;
  }

  handlePatientProfileInformationApplied(info: PatientProfileInformation): boolean {
    try {
      if (!info || !info.items) { return false; }
      if (!this.name || !this.isEditable || this.isHisInfo) { return false; }
      const found = info.items.find(item => item.name === this.name);
      if (!found) { return false; }
      const fuzzyDate = this.value as FuzzyDate;
      // 時刻に14文字入っていて外部処理のときクリアする判定と競合するので対策している
      this.value = new FuzzyDate(this.textToDate(found.value, false), fuzzyDate.isTime);
      return true;
    } catch (e) {
      console.error("患者プロフィールの値の変換に失敗しました。(DateItem)");
      console.error(e);
    }
    return false;
  }

  textToDate(source: string, shouldValidateValueLength: boolean = true): Date | string {
    if (shouldValidateValueLength && source && source.length >= 11) { return null; }　
    const sourceText = (source + "00000000000000").slice(0, 14);
    if (!sourceText.match(/^[0-9]{14}$/)) { return source; }
    if (sourceText === "00000000000000") { return null; }
    const dateTimeText = sourceText.slice(0, 8) + "T" + sourceText.slice(8);
    const dateTime = Moment(dateTimeText).toDate();
    return dateTime.toString() === "Invalid Date"
      ? source
      : dateTime;
  }

  textToTime(source: string): Date | string {
    const sourceText = (source + "000000").slice(0, 6);
    if (!sourceText.match(/^[0-9]{6}$/)) { return source; }
    const dateTime = Moment(sourceText, "HHmmss").toDate();
    return dateTime.toString() === "Invalid Date"
      ? source
      : dateTime;
  }

  createPatientProfileItem(): PatientProfileItem {
    if (!this.name || this.isHisInfo) { return null; }
    const fuzzyDate = this.value as FuzzyDate;
    const item = new PatientProfileItem();
    item.name = this.name;
    item.value = fuzzyDate.isTime
      ? (fuzzyDate.isEmptyOrZeroLength ? "00000000000000" : this.value.toString("YYYYMMDDHHmmss"))
      : (fuzzyDate.isEmptyOrZeroLength ? "00000000" : this.value.toString("YYYYMMDD"));
    return item;
  }

  adjustFontSize(): void {
    if (!this.enabledAutoAdjustFontSize) { return; }
    this.textStyle.adjustedFontSize = this.getAdjustedFontSize(this.textStyle.isMultiLine);
  }

  pasteTo(item: IPasteApplicable): void {
    const text = this.valueToText(this.value);
    item.applyPastedValue(text, undefined);
  }

  get isSuppressionActive(): boolean {
    return this.isEmpty;
  }

  public setText(text: string): void {
    this.value.value = text;
    this.value.linkedDate = null;
  }

  isValueUpdated(item: any): boolean {
    if (!item) { return true; }
    try {
      if (this.isEmpty && item.isEmpty) { return false; }
      // Dateの場合はインスタンス比較では判定できないので対策している
      if ((this.value.value instanceof Date) && (item.value.value instanceof Date)) {
        return (this.value.value.getTime() !== item.value.value.getTime());
      } else {
        return (this.value.value !== item.value.value) || (this.value.linkedDate !== item.value.linkedDate);
      }
    } catch (e) {
      // Exceptionは発生しない見込みだが、念のためcatchしておく
      console.warn(e);
      return true;
    }
  }

  changeInputMethodExceptUserDefinitionSelect(): void {
    this.inputMethodKind = DateInputMethodKinds.Calendar;
    this.linkedValue = "";
  }

  getDisplayName(): string {
    if (this.userDefinitionKey && this.itemName) { return this.itemName; }
    return this.isHisInfo ? this.displayName : this.name;
  }

  get textToCopy(): string {
    return this.valueToText(this.value);
  }

  rollbackValue(): void {
    if (this.inputMethodKind === DateInputMethodKinds.None) {
      this.value = this.cloneValue(this.originalValue);
    }
  }

  toDocumentDataDragParameter(): IDocumentDataDragParameter {
    const value = this.value as FuzzyDate;
    if (this.inputMethodKind === DateInputMethodKinds.FreeInput || value == null || !(value.value instanceof Date)) { return null; }
    return {
      text: this.text(),
      date: value.value,
      isTime: value.isTime,
      format: this.format,
    };
  }

  setDroppedDocumentData(text: string, param: IDocumentDataDragParameter): void {
    // オブジェクトがフリー入力状態の場合はテキストを張り付ける
    // ただ、TextFreeInputableMixin内で分岐しているので実際にはここは通らない。念のための実装。
    if (this.inputMethodKind === DateInputMethodKinds.FreeInput) {
      console.warn("過去データ貼り付けの処理で意図していないパスを通っています。実装を確認してください。");
      this.value = new FuzzyDate(this.text() + text, this.value.isTime);
      return;
    }
    // 書式が一致している場合はカレンダー入力のまま日付を格納する
    // TODO: 独自指定の書式も比較したほうがよい？
    if (param && param.format && this.format === param.format && this.value.isTime === param.isTime && this.isCalendarInputtable) {
      this.updateInputMethodKind(DateInputMethodKinds.Calendar, false);
      this.value = new FuzzyDate(param.date, this.value.isTime);
      return;
    }
    // それ以外のケースはフリー入力に変更してテキストを張り付ける
    // 既存のテキストは破棄する（V1と同様の挙動）
    if (this.isFreeInputable) {
      this.updateInputMethodKind(DateInputMethodKinds.FreeInput, false);
      this.value = new FuzzyDate(text, this.value.isTime);
    }
  }

  setFixedDate(value: Date | string): void {
    if (this.inputMethodKind !== DateInputMethodKinds.Calendar) {
      this.updateInputMethodKind(DateInputMethodKinds.Calendar, false);
    }
    this.value = new FuzzyDate(value, this.value.isTime);
  }

  setUserDefinitionLinkValues(values: string[]): void {
    if (!values || values.length === 0) {
      this.linkedValue = null;
      return;
    }
    // 複数選択された場合は、先頭の値を採用する（そもそもDMOのWebEditorは複数選択できない）
    this.linkedValue = values[0];
  }

  getNumberingKeyword(): string {
    // LinkActionsが指定されたユーザ定義がフリー入力になっている場合は、
    // 登録前にUserDefinitionLinkValueApplicatorでlinkedValueを格納している
    return (this.userDefineLinkActions.length === 0)
      ? this.text()
      : this.linkedValue;
  }

  setSelectedItems(items: any[]): void {
    this.selectedItems = items;
  }

  isDateLinkKeyMatch(parameter: RelationalActionParameter): boolean {
    return (!!parameter.key && parameter.key === this.name && parameter.subKey === null);
  }

  applyDateTo(item: IDateLinkApplicable): void {
    // V1では「**時頃」のボタンを押したときも日付が連動できている
    // 現状のDocu2Webでも文書編集画面内であれば同様の挙動をできるようにはしているが、
    // V1ではreport.xml内に日付の情報も持っているのに対してDocu2Webでは持っていないので、文書登録後の編集では日付を維持できない
    // 必要あればデータの持ち方の見直し・移行も含めて検討が必要
    const fuzzyDate = this.value as FuzzyDate;
    const linkedDate = fuzzyDate.isFuzzy
      ? fuzzyDate.linkedDate
      : fuzzyDate.date;
    item.applyDate(linkedDate);
  }

  applyDate(date: Date): void {
    const fuzzyDate = this.value as FuzzyDate;
    this.value = new FuzzyDate(date, fuzzyDate.isTime);
  }

  /**
   * 引用・転記の処理でカレンダー入力に切り替えるとき、setter経由だと
   * 人の手により操作されたと判定されてしまいカレンダーダイアログが出てしまうのでメソッド追加している
   */
  setInputMethodKindAsInternal(value: DateInputMethodKinds) {
    this.updateInputMethodKind(value, false);
  }

  get currentNaturalText(): string {
    return this.isNaturalViewEnable && this.naturalText
      ? this.naturalText
      : this.text();
  }

  /**
   * 日付比較のため、時刻情報を除外したDateのgetTime()を返却している
   * 時刻オブジェクトについてはそもそもフォーマット作成時に対象とできないようなので非対応としている
   */
  extractValueForDateCompare(): number {
    const fuzzyDate = this.value as FuzzyDate;
    if (this.shouldInputFree || !fuzzyDate || fuzzyDate.isTime || !fuzzyDate.date) {
      return null;
    }
    const convertedDate = new Date(fuzzyDate.date.getFullYear(), fuzzyDate.date.getMonth(), fuzzyDate.date.getDate());
    return convertedDate.getTime();
  }
}
